import { Link } from "react-router-dom";
import { BBQNav } from "@/components/bbq/BBQNav";
import { BBQFooter } from "@/components/bbq/BBQFooter";
import { BBQQuoteForm } from "@/components/bbq/BBQQuoteForm";
import { BBQMobilePhoneCTA } from "@/components/bbq/BBQMobilePhoneCTA";
import { Button } from "@/components/ui/button";
import { Flame, ChevronRight, Star, MapPin, Phone, Truck, Shield, Award, Clock, Palette } from "lucide-react";
import { bbqResourceLinks } from "@/lib/resourceLinks";
import { COMPANY_INFO } from "@/lib/brandConfig";
import { BBQ_CATEGORIES } from "@/lib/bbqCategories";
import { SEOHead } from "@/components/seo/SEOHead";
import { LocalBusinessSchema, WebsiteSchema, FAQSchema } from "@/components/seo/LocalBusinessSchema";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { bbqFAQs, showroomFAQs } from "@/lib/seo/faqData";
import { BBQSavingsModal } from "@/components/bbq/BBQSavingsModal";
import BrandShowcase from "@/components/shared/BrandShowcase";
import ResourcesGrid from "@/components/shared/ResourcesGrid";
import { QuoteTriggerButton } from "@/components/quote/QuoteTriggerButton";
import { TextUsButton } from "@/components/shared/TextUsButton";
import { DesignWizardCTA } from "@/components/bbq/DesignWizardCTA";

// Import optimized WebP images
import bbqHeroImage from "@/assets/bbq-hero-compressed.webp";
import bbqHeroMobile from "@/assets/bbq-hero-mobile.webp";
import bbqCategoryGrills from "@/assets/bbq-category-grills.webp";
import bbqCategorySmokers from "@/assets/bbq-category-smokers.webp";
import bbqCategoryPizzaOvens from "@/assets/bbq-category-pizza-ovens.webp";
import bbqCategoryIslands from "@/assets/bbq-category-islands.webp";
import bbqCategoryRefrigeration from "@/assets/bbq-category-refrigeration.webp";

const categoryImages: Record<string, string> = {
  "outdoor-kitchens": bbqCategoryIslands,
  "grills": bbqCategoryGrills,
  "smokers": bbqCategorySmokers,
  "pizza-ovens": bbqCategoryPizzaOvens,
  "refrigeration": bbqCategoryRefrigeration,
  "accessories": bbqCategoryIslands,
};

const featuredProducts = [
  { name: "6-Burner Grill Island", savingsPercent: 61, image: bbqCategoryIslands, tag: "Best Seller", category: "outdoor-kitchens" },
  { name: "Premium Built-In Grill", savingsPercent: 60, image: bbqCategoryGrills, tag: "Hot Deal", category: "grills" },
  { name: "Wood Fired Pizza Oven", savingsPercent: 59, image: bbqCategoryPizzaOvens, tag: "Popular", category: "pizza-ovens" },
  { name: "Pellet Smoker Pro", savingsPercent: 61, image: bbqCategorySmokers, tag: "New", category: "smokers" },
  { name: "Outdoor Refrigerator", savingsPercent: 58, image: bbqCategoryRefrigeration, tag: "Value", category: "refrigeration" },
  { name: "L-Shaped Kitchen Island", savingsPercent: 60, image: bbqCategoryIslands, tag: "Premium", category: "outdoor-kitchens" },
];

const BBQHomePage = () => {
  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <SEOHead
        title="BBQ Island Liquidators | Outdoor Kitchens 60-70% Off | Dallas TX"
        description="Factory direct BBQ islands and outdoor kitchens at 60-70% off retail. Premium built-in grills, smokers, and pizza ovens. Visit our Carrollton showroom!"
        canonical="https://bbqislandliquidators.com"
        brand="bbq"
        keywords={["BBQ islands Dallas", "outdoor kitchens DFW", "BBQ island liquidators", "factory direct outdoor kitchens", "discount BBQ grills Texas"]}
      />
      <WebsiteSchema
        name="BBQ Island Liquidators"
        url="https://bbqislandliquidators.com"
        description="Factory direct BBQ islands and outdoor kitchens at 60-70% off retail in Dallas-Fort Worth."
      />
      <LocalBusinessSchema
        type="HomeAndConstructionBusiness"
        name="BBQ Island Liquidators"
        description="Factory direct BBQ islands and outdoor kitchens at 60-70% off retail. Premium built-in grills, smokers, and pizza ovens in Dallas-Fort Worth."
        url="https://bbqislandliquidators.com"
      />
      <FAQSchema items={[...bbqFAQs.slice(0, 4), ...showroomFAQs.slice(0, 2)]} />
      <BBQSavingsModal />
      <BBQNav />

      <main>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* LCP Image - responsive srcset for mobile performance */}
        <img 
          src={bbqHeroImage}
          srcSet={`${bbqHeroMobile} 768w, ${bbqHeroImage} 1920w`}
          sizes="100vw"
          alt="Premium outdoor kitchen with built-in BBQ grill and stainless steel appliances"
          fetchPriority="high"
          decoding="async"
          width={1920}
          height={1080}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        
        <div className="relative container mx-auto px-4 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-3 bg-orange-500/20 border-2 border-orange-500 text-orange-400 px-5 py-2 rounded mb-8">
              <Flame className="w-5 h-5" />
              <span className="font-black text-sm uppercase tracking-widest">Factory Direct Prices</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-[#f5e6d3] leading-none mb-6">
              BUILD YOUR
              <span className="block text-orange-500">DREAM</span>
              <span className="block font-serif italic font-normal">
                Outdoor Kitchen
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-[#f5e6d3]/80 mb-10 max-w-xl leading-relaxed">
              DFW's premier outdoor kitchen specialists. Premium BBQ islands, grills, smokers & more at 
              <span className="text-orange-400 font-bold"> up to 65% off</span> retail.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/outdoor-kitchens">
                <Button 
                  size="lg" 
                  className="bg-orange-500 hover:bg-orange-600 text-white font-black py-7 px-10 text-lg uppercase tracking-wide"
                >
                  Shop Now <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/design-wizard">
                <Button 
                  size="lg" 
                  className="bg-white text-[#1a1a1a] hover:bg-gray-100 font-bold py-7 px-10 text-lg uppercase tracking-wide"
                >
                  <Palette className="w-5 h-5 mr-2" /> Design in 3D
                </Button>
              </Link>
            </div>

            <div className="flex items-center gap-8 mt-12 pt-8 border-t border-[#f5e6d3]/20">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-orange-400 text-orange-400" />
                  ))}
                </div>
                <span className="text-[#f5e6d3]/80 text-sm">500+ Reviews</span>
              </div>
              <div className="text-[#f5e6d3]/80 text-sm">
                <span className="text-orange-400 font-bold">10+</span> Years Experience
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#2a2520]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-[#f5e6d3] mb-4">
              WHY <span className="text-orange-500">BBQ ISLAND</span> LIQUIDATORS?
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, title: "Factory Direct", desc: "Cut out the middleman and save thousands" },
              { icon: Shield, title: "Lifetime Warranty", desc: "Premium islands backed by lifetime guarantee" },
              { icon: Truck, title: "Free Delivery", desc: "White glove delivery across DFW" },
              { icon: Clock, title: "Expert Team", desc: "10+ years of outdoor kitchen experience" },
            ].map((item) => (
              <div 
                key={item.title} 
                className="bg-[#1a1a1a] border-l-4 border-orange-500 p-6 hover:transform hover:-translate-y-1 transition-transform"
              >
                <item.icon className="w-8 h-8 text-orange-500 mb-4" />
                <h3 className="text-xl font-black text-[#f5e6d3] mb-2">{item.title}</h3>
                <p className="text-[#f5e6d3]/60">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Showcase */}
      <BrandShowcase site="bbq" variant="dark" />

      {/* Shop by Category */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-orange-500 font-bold uppercase tracking-widest text-sm">Shop by Category</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#f5e6d3] mt-2">
              EXPLORE OUR COLLECTION
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BBQ_CATEGORIES.slice(0, 6).map((category) => (
                <Link 
                  key={category.slug}
                  to={`/${category.slug}`}
                  className="group relative overflow-hidden"
                >
                  <OptimizedImage 
                    src={categoryImages[category.slug]} 
                    alt={category.name}
                    width={400}
                    height={300}
                    className="aspect-[4/3] group-hover:scale-110 transition-transform duration-700"
                  />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-white/70 text-sm mb-3">{category.description}</p>
                  <div className="flex items-center gap-2 text-orange-400 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                    Shop Now <ChevronRight className="w-4 h-4" />
                  </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Design Wizard CTA Banner */}
      <DesignWizardCTA variant="banner" />

      {/* Featured Products */}
      <section className="py-20 bg-[#2a2520]">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="text-orange-500 font-bold uppercase tracking-widest text-sm">Limited Stock</span>
              <h2 className="text-4xl md:text-5xl font-black text-[#f5e6d3] mt-2">
                HOT DEALS 🔥
              </h2>
            </div>
            <Link to="/clearance" className="text-orange-400 hover:text-orange-300 font-bold flex items-center gap-2 mt-4 md:mt-0">
              View All Deals <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProducts.map((product) => (
              <div 
                key={product.name}
                className="bg-[#1a1a1a] group overflow-hidden hover:ring-2 hover:ring-orange-500 transition-all"
              >
                <Link to={`/${product.category}`} className="block">
                  <div className="relative overflow-hidden">
                    <OptimizedImage 
                      src={product.image}
                      alt={product.name}
                      width={400}
                      height={300}
                      className="aspect-[4/3] group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4 bg-orange-500 text-white px-3 py-1 text-xs font-black uppercase tracking-wider">
                      {product.tag}
                    </div>
                    <div className="absolute bottom-4 right-4 bg-black/80 text-orange-400 px-3 py-1 text-sm font-bold">
                      SAVE {product.savingsPercent}%
                    </div>
                  </div>
                </Link>
                
                <div className="p-6">
                  <Link to={`/${product.category}`}>
                    <h3 className="text-xl font-bold text-[#f5e6d3] mb-3 group-hover:text-orange-400 transition-colors">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="text-sm text-[#f5e6d3]/60 mb-4">
                    Showroom pricing only — visit or call for your personalized quote.
                  </p>
                  <QuoteTriggerButton 
                    className="w-full bg-[#2a2520] hover:bg-orange-500 text-[#f5e6d3] hover:text-white border border-[#f5e6d3]/20 hover:border-orange-500 font-bold transition-all"
                    category={product.category === 'outdoor-kitchens' ? 'Outdoor Kitchen' : product.category === 'pizza-ovens' ? 'Pizza Oven' : 'BBQ Island'}
                  >
                    GET QUOTE
                  </QuoteTriggerButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local DFW Section */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-orange-500 font-bold uppercase tracking-widest text-sm">Local DFW Experts</span>
              <h2 className="text-4xl md:text-5xl font-black text-[#f5e6d3] mt-2 mb-6">
                WHY BUY LOCAL IN <span className="text-orange-500">DALLAS-FORT WORTH</span>?
              </h2>
              <div className="space-y-6 text-[#f5e6d3]/80">
                <p>
                  Unlike national online retailers, BBQ Island Liquidators offers a hands-on showroom experience 
                  right here in Carrollton. See, touch, and compare outdoor kitchens before you buy.
                </p>
                <ul className="space-y-4">
                  {[
                    "15,000 sq ft showroom with working displays",
                    "Free design consultation with local BBQ experts",
                    "White-glove delivery & installation throughout DFW",
                    "Ongoing support from neighbors who understand Texas outdoor living",
                    "No shipping damage risk—local pickup or professional delivery",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Flame className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="font-semibold text-[#f5e6d3]">
                  Serving Dallas, Fort Worth, Plano, Frisco, McKinney, Allen, Carrollton, Irving, Grapevine, 
                  Southlake, Arlington, Flower Mound, Lewisville, Richardson, The Colony, Prosper, Celina & all DFW.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/showroom">
                  <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-bold">
                    <MapPin className="w-5 h-5 mr-2" /> Visit Our Showroom
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <OptimizedImage 
                src={bbqCategoryIslands}
                alt="BBQ Island Liquidators Showroom"
                width={600}
                height={450}
                className="w-full rounded-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white p-6 max-w-xs">
                <p className="font-bold text-lg mb-2">Visit Our Showroom</p>
                <p className="text-sm text-white/90">{COMPANY_INFO.address.full}</p>
                <a href={`tel:${COMPANY_INFO.phone}`} className="text-white font-bold mt-2 block">
                  {COMPANY_INFO.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ResourcesGrid
        variant="bbq"
        title={bbqResourceLinks.title}
        links={bbqResourceLinks.links}
      />

      {/* CTA + Quote Form */}
      <section className="py-20 bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="text-white">
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                READY TO FIRE IT UP?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Get a free, no-obligation quote on your dream outdoor kitchen. Our experts will help you 
                design the perfect setup for your backyard and budget.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 flex items-center justify-center rounded">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-bold">Showroom</p>
                    <p className="text-white/80">{COMPANY_INFO.address.full}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 flex items-center justify-center rounded">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-bold">Call Us</p>
                    <a href={`tel:${COMPANY_INFO.phone}`} className="text-white/80 hover:text-white">
                      {COMPANY_INFO.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 flex items-center justify-center rounded">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-bold">Hours</p>
                    <p className="text-white/80">{COMPANY_INFO.hours.weekday} | {COMPANY_INFO.hours.weekend}</p>
                  </div>
                </div>
              </div>
            </div>
            <BBQQuoteForm variant="full" />
          </div>
        </div>
      </section>
      </main>

      <TextUsButton />
      <BBQMobilePhoneCTA />
      <BBQFooter />
    </div>
  );
};

export default BBQHomePage;
